import React, { useEffect, useCallback, useMemo, useState } from "react";
import Search from "@dtci/spellbook/Search";
import { useParams, useNavigate } from "react-router-dom";
import Typography from "@dtci/spellbook/Typography";
import Button from "@dtci/spellbook/Button";
import Card from "@dtci/spellbook/Card";
import Icon from "@dtci/spellbook/Icon";
import Badge from "@dtci/spellbook/Badge";
import BadgedContainer from "@dtci/spellbook/BadgedContainer";
import { PPTStatusLifecycle } from "../components/PPTStatusLifecycle";
import { PPTStatTiles } from "../components/PPTStatTiles";
import {
  fetchPromotion,
  fetchPromotionDiscounts,
  fetchPromotionDeliveries,
  fetchPromotionEvents,
  fetchPromotionIssues,
} from "ppt/api";
import { usePPTActions, usePPTState } from "tap/store/ppt/usePPT";
import { usePPTToasts } from "tap/pages/PPT/hooks/usePPTToasts";
import { usePPTAuthorization } from "tap/pages/PPT/hooks/usePPTAuthorization";
import { GRID_PREFERENCES_STATUS } from "ppt/constants";
import { buildNavTo } from "tap/components/PageNavigation/navStorage";
import PPTToastNotificationProvider from "../components/PPTToastNotifications";
import Skeleton from "@dtci/spellbook/Skeleton";
import PopoverButton from "@dtci/spellbook/PopoverButton";
import MenuList from "@dtci/spellbook/MenuList";
import { useAgGridApi } from "tap/components/AGGrid/hooks";
import { PPTDiscountDetailsGrid } from "../components/PPTDiscountDetailsGrid";
import { PPTDiscountDrawer } from "../components/PPTDiscountDrawer";
import { PPTSplitButton } from "../components/PPTSplitButton";
import { PPTClientDiscountDrawer } from "../components/PPTClientDiscountDrawer";
import { PPTDeliveryDrawer } from "../components/PPTDeliveryDrawer";
import { PPTPromoSummary } from "../components/PPTPromoSummary";
import { PPTAuditLogGrid } from "../components/PPTAuditLogGrid";
import { PPTPromotionActionsMenu } from "../components/PPTPromotionActionsMenu";
import { PPTDeliveriesPanel } from "../components/PPTDeliveriesPanel";
import { PPTPromotionIssueList } from "../components/PPTPromotionIssueList";
import PPTStatusCellRenderer from "../components/PPTPromosGrid/cell-renderers/PPTStatusCellRenderer";
import {
  computeRollup,
  rollupToStatTileCounts,
} from "../components/PPTStatusLifecycle/lifecycleRollup";
import "../styles.scss";
import styles from "./pptPromotionPage.module.scss";

const VIEW_OPTION_RESET_COLUMNS = "reset-columns";

export const PPTPromotionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    updatePromotionDetail,
    setPromotionDetailLoading,
    updateDiscountDetailsData,
    setDiscountDetailsLoading,
    setDiscountDetailsSearch,
    updatePromotionEvents,
    setPromotionEventsLoading,
    setPromotionEventsSearch,
    setPromotionEventsPagination,
    updatePromotionIssues,
    setPromotionIssuesLoading,
    updatePromotionDeliveries,
    setPromotionDeliveriesLoading,
    resetPromotionDetail,
    setLifecycleFilter,
  } = usePPTActions();
  const {
    promotionDetail,
    promotionDetailLoading,
    discountDetails,
    discountDetailsLoading,
    discountDetailsSearch,
    promotionEventsSearch,
    promotionEventsPagination,
    lifecycleFilter,
    gridPreferencesStatus,
  } = usePPTState();
  const { showErrorToast } = usePPTToasts();
  const titleDetailsGridApi = useAgGridApi();
  const auditLogGridApi = useAgGridApi();
  const [discountGridControls, setDiscountGridControls] = useState(null);

  // Both discount lenses restore their saved column layout from a snapshot taken
  // on first render, so the grid must not mount until the preferences fetch has
  // settled — an early mount would freeze an empty layout and never restore.
  // Fetched once per session by PPMPreferencesGate; "settled" includes a failed
  // fetch, which lands on LOADED with empty preferences so this can't wedge.
  const preferencesReady =
    gridPreferencesStatus === GRID_PREFERENCES_STATUS.LOADED;

  const rollup = useMemo(
    () =>
      computeRollup(discountDetails, promotionDetail?.discountCountByStatus),
    [discountDetails, promotionDetail?.discountCountByStatus],
  );

  const statTileCounts = useMemo(
    () => rollupToStatTileCounts(rollup),
    [rollup],
  );

  const { hasPptPermission, isPptAdmin } = usePPTAuthorization();

  const onFilterButtonClick = useCallback(() => {
    titleDetailsGridApi.AgGridApiRef.current.api.isToolPanelShowing("filters")
      ? titleDetailsGridApi.AgGridApiRef.current.api.closeToolPanel("filters")
      : titleDetailsGridApi.AgGridApiRef.current.api.openToolPanel("filters");
  }, [titleDetailsGridApi]);

  const onClearAllFilters = useCallback(() => {
    setLifecycleFilter(null);
    setDiscountDetailsSearch("");
    const api = titleDetailsGridApi.AgGridApiRef.current?.api;
    if (api) {
      api.setFilterModel(null);
    }
  }, [setLifecycleFilter, setDiscountDetailsSearch, titleDetailsGridApi]);

  const handleResetDiscountColumns = useCallback(async () => {
    try {
      await discountGridControls?.resetColumnState();
    } catch {
      showErrorToast("Failed to reset grid layout. Please try again.");
    }
  }, [discountGridControls, showErrorToast]);

  const canResetDiscountColumns = Boolean(
    discountGridControls?.isGridReady &&
      discountGridControls?.hasSavedColumnState,
  );

  const discountViewOptions = useMemo(
    () => [
      {
        value: VIEW_OPTION_RESET_COLUMNS,
        label: "Reset column layout",
        isDisabled: !canResetDiscountColumns,
      },
    ],
    [canResetDiscountColumns],
  );

  const handleDiscountViewOptionSelect = useCallback(
    (option) => {
      if (
        option?.value === VIEW_OPTION_RESET_COLUMNS &&
        canResetDiscountColumns
      ) {
        handleResetDiscountColumns();
      }
    },
    [canResetDiscountColumns, handleResetDiscountColumns],
  );

  const onDiscountDetailsSearch = useCallback(
    (search) => {
      setDiscountDetailsSearch(search);
    },
    [setDiscountDetailsSearch],
  );

  const onAuditLogSearch = useCallback(
    (search) => {
      setPromotionEventsSearch(search);
    },
    [setPromotionEventsSearch],
  );

  const loadPromo = useCallback(async () => {
    setPromotionDetailLoading(true);
    try {
      const { data } = await fetchPromotion(id);
      updatePromotionDetail(data);
    } catch (err) {
      console.error("Error fetching promotion details:", err);
      showErrorToast("Error fetching Promotion. Please try again later.");
    } finally {
      setPromotionDetailLoading(false);
    }
  }, [id, updatePromotionDetail, showErrorToast, setPromotionDetailLoading]);

  const loadDiscounts = useCallback(async () => {
    setDiscountDetailsLoading(true);
    try {
      const data = await fetchPromotionDiscounts(id);
      updateDiscountDetailsData(data);
    } catch (err) {
      console.error("Error fetching promotion discounts:", err);
      showErrorToast("Error fetching Discounts. Please try again later.");
    } finally {
      setDiscountDetailsLoading(false);
    }
  }, [id, updateDiscountDetailsData, showErrorToast, setDiscountDetailsLoading]);

  const loadEvents = useCallback(
    async (paginationParams) => {
      setPromotionEventsLoading(true);
      try {
        const params = paginationParams || { offset: 0, limit: 50 };
        const data = await fetchPromotionEvents(id, params);
        updatePromotionEvents(data?.events || []);
        setPromotionEventsPagination({
          total: data?.total || 0,
          offset: data?.offset || 0,
          limit: data?.limit || 50,
        });
      } catch (err) {
        console.error("Error fetching promotion events:", err);
        showErrorToast("Error fetching audit events. Please try again later.");
      } finally {
        setPromotionEventsLoading(false);
      }
    },
    [
      id,
      updatePromotionEvents,
      setPromotionEventsPagination,
      showErrorToast,
      setPromotionEventsLoading,
    ],
  );

  const onAuditLogPaginationChange = useCallback(
    (paginationParams) => {
      loadEvents(paginationParams);
    },
    [loadEvents],
  );

  const loadIssues = useCallback(async () => {
    setPromotionIssuesLoading(true);
    try {
      const data = await fetchPromotionIssues(id);
      updatePromotionIssues(data);
    } catch (err) {
      console.error("Error fetching promotion issues:", err);
      showErrorToast("Error loading issues. Please try again later.");
    } finally {
      setPromotionIssuesLoading(false);
    }
  }, [id, updatePromotionIssues, showErrorToast, setPromotionIssuesLoading]);

  const loadDeliveries = useCallback(async () => {
    setPromotionDeliveriesLoading(true);
    try {
      const data = await fetchPromotionDeliveries(id);
      updatePromotionDeliveries(data);
    } catch (err) {
      console.error("Error fetching promotion deliveries:", err);
      showErrorToast("Error loading deliveries. Please try again later.");
    } finally {
      setPromotionDeliveriesLoading(false);
    }
  }, [
    id,
    updatePromotionDeliveries,
    showErrorToast,
    setPromotionDeliveriesLoading,
  ]);

  useEffect(() => {
    // Redirect unauthorized users to home
    if (!hasPptPermission) {
      navigate("/home", { replace: true });
      return;
    }
  }, [hasPptPermission, navigate]);

  useEffect(() => {
    if (hasPptPermission) {
      // Fetch all data in parallel on initial load
      Promise.all([
        loadPromo(),
        loadDiscounts(),
        loadIssues(),
        loadDeliveries(),
      ]);
    }
  }, [hasPptPermission, loadPromo, loadDiscounts, loadIssues, loadDeliveries]);

  // Clear state on unmount so we're ready for the next promotion detail view
  useEffect(() => {
    return () => {
      resetPromotionDetail();
    };
  }, [resetPromotionDetail]);

  // Return to the promo list with its saved sort/filters, rather than a bare
  // /ppt. buildNavTo is used over navigate(-1) so this still lands on the list
  // when the user arrived here by deep link, bookmark or refresh.
  const onBackToAllPromos = useCallback(() => {
    navigate(buildNavTo("/ppt"));
  }, [navigate]);

  return (
    <section className="ppt-scrolling-page-container">
      <Button
        styleType="link"
        iconPlacement="before"
        iconType="fa-chevron-left"
        colorType="base"
        style={{ marginLeft: 0, marginBottom: 25 }}
        onClick={onBackToAllPromos}
      >
        Back to All Promos
      </Button>
      <section className={styles.promotionHeader}>
        <Skeleton isLoading={promotionDetailLoading}>
          <BadgedContainer
            className={styles.promotionTitleContainer}
            badges={[
              ...(rollup?.activeErrors > 0
                ? [
                    {
                      position: "top-right",
                      component: (
                        <Badge
                          shape="circle"
                          size="small"
                          color="error"
                          light
                          title={`${rollup.activeErrors} ${rollup.activeErrors === 1 ? "discount has an error" : "discounts have errors"}`}
                        >
                          {rollup.activeErrors}
                        </Badge>
                      ),
                    },
                  ]
                : []),
              ...(rollup?.activeWarnings > 0
                ? [
                    {
                      position: "top-right",
                      component: (
                        <Badge
                          shape="circle"
                          size="small"
                          color="warning"
                          light
                          title={`${rollup.activeWarnings} warning${rollup.activeWarnings === 1 ? "" : "s"}`}
                        >
                          {rollup.activeWarnings}
                        </Badge>
                      ),
                    },
                  ]
                : []),
            ]}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Typography variant="T50">
                {promotionDetail?.name || "Promotion Details"}
              </Typography>
              {promotionDetail?.hasPendingChange && (
                <Badge
                  shape="status"
                  size="smaller"
                  color="error"
                  title="This promotion has pending changes"
                  style={{ marginBottom: "0.5rem" }}
                >
                  Changes
                </Badge>
              )}
            </div>
          </BadgedContainer>
        </Skeleton>
        <div className={styles.headerActions}>
          {isPptAdmin && id && (
            <PPTPromotionActionsMenu
              promotionId={id}
              onActionComplete={({ action }) => {
                if (action === "revalidate") {
                  // Re-validate is synchronous — refresh promotion + issues + discounts
                  // immediately so the UI reflects the new verdict.
                  loadPromo();
                  loadDiscounts();
                  loadIssues();
                  loadEvents();
                } else if (action === "resync") {
                  // Re-sync is async — the worker drains over the next minute.
                  // Refresh the audit log so the operator can see the
                  // EnrichmentResyncRequested event was recorded.
                  loadEvents();
                }
              }}
            />
          )}
        </div>
      </section>
      <section>
        <PPTStatusLifecycle />
      </section>
      <PPTPromotionIssueList />
      <div className="ppt-cards-container">
        <Card>
          <h4>
            <Icon iconType="fa" faPackage="fal" iconName="fa-file" />
            Promo Summary
          </h4>
          <PPTPromoSummary />
        </Card>
        <Card>
          <h4>
            <Icon iconType="fa" faPackage="fal" iconName="fa-file-alt" />
            Discount Details
          </h4>
          <div className={styles.discountDetailsHeader}>
            <div className={styles.discountDetailsSearch}>
              <Search
                value={discountDetailsSearch}
                labelText="Search discounts"
                onChange={onDiscountDetailsSearch}
                width="auto"
              />
            </div>
            <div className={styles.discountDetailsFilters}>
              <PPTStatTiles
                counts={statTileCounts}
                selectedFilter={lifecycleFilter}
                onSelect={setLifecycleFilter}
              />
              <div className={styles.filterButtonGroup}>
                <Button
                  styleType="standard"
                  iconPlacement="before"
                  iconType="fa-filter"
                  onClick={onFilterButtonClick}
                >
                  Filters
                </Button>
                <PPTSplitButton
                  primaryButton={
                    <Button
                      styleType="standard"
                      iconPlacement="before"
                      iconType="fa-times"
                      onClick={onClearAllFilters}
                    >
                      Clear All Filters
                    </Button>
                  }
                  dropdownButton={
                    <PopoverButton
                      buttonIcon="fa-chevron-down"
                      buttonProps={{
                        styleType: "standard",
                        sizeType: "medium",
                        iconOnly: true,
                        title: "More view options",
                      }}
                      testMetadata={{
                        button: {
                          "data-testid": "discount-view-options-toggle",
                        },
                      }}
                    >
                      <MenuList
                        options={discountViewOptions}
                        onChange={handleDiscountViewOptionSelect}
                        isStandalone
                      />
                    </PopoverButton>
                  }
                />
              </div>
            </div>
          </div>
          <Skeleton isLoading={discountDetailsLoading || !preferencesReady}>
            <div style={{ height: 600 }}>
              <PPTDiscountDetailsGrid
                gridRef={titleDetailsGridApi.AgGridApiRef}
                onColumnStateChange={setDiscountGridControls}
              />
            </div>
          </Skeleton>
        </Card>
        <PPTDeliveriesPanel promotionId={id} />
        <Card>
          <h4>
            <Icon iconType="fa" faPackage="fal" iconName="fa-clock" />
            Audit Log
          </h4>
          <div className={styles.auditLogHeader}>
            <div className={styles.auditLogSearch}>
              <Search
                value={promotionEventsSearch}
                labelText="Search audit events"
                onChange={onAuditLogSearch}
                width="auto"
              />
            </div>
          </div>
          <Skeleton isLoading={promotionDetailLoading}>
            <div style={{ height: 800 }}>
              <PPTAuditLogGrid
                gridRef={auditLogGridApi.AgGridApiRef}
                onPaginationChange={onAuditLogPaginationChange}
              />
            </div>
          </Skeleton>
        </Card>
      </div>
      <PPTDiscountDrawer />
      <PPTClientDiscountDrawer />
      <PPTDeliveryDrawer />
      <PPTToastNotificationProvider />
    </section>
  );
};
